import React from 'react';
import Button from '../button/Button';
import Dropdown, { Option } from '../dropdown/Dropdown';
import Tooltip from '../tooltip/Tooltip';
import { useRuleEngine } from './hooks/useRuleEngine';
import { RuleEngineBindings, RuleEngineConfig } from './models/RuleEngineConfig';
import { RuleEngineState } from './models/RuleEngineState';

export interface RuleEngineProps {
  config: RuleEngineConfig;
  initialState: RuleEngineState;
  bindings: RuleEngineBindings;
}
export const RuleEngineV2: React.FC<RuleEngineProps> = ({ config, bindings, initialState }) => {
  const ruleEngine = useRuleEngine(config, bindings, initialState);

  return (
    <div className="rule-engine-v2">
      {ruleEngine.state?.ruleGroups?.length > 1 && !bindings.hideMatchAllRuleGroupsTab && (
        <div className="rule-engine-v2__match-all-any__group">
          <div className={'rule-engine-v2__match-all-any large'}>
            <div
              onClick={() => ruleEngine.toggleMatchAllRuleGroups(true)}
              className={`rule-engine-v2__match-all-any__button ${!bindings.viewMode && 'clickable'} ${ruleEngine.state.matchAllRuleGroups && 'selected'}`}>
              Match all rule groups
            </div>
            <div
              onClick={() => ruleEngine.toggleMatchAllRuleGroups(false)}
              className={`rule-engine-v2__match-all-any__button ${!bindings.viewMode && 'clickable'} ${!ruleEngine.state.matchAllRuleGroups && 'selected'}`}>
              Match any rule groups
            </div>
          </div>
        </div>
      )}
      {ruleEngine.state?.ruleGroups?.length > 0 && (
        <div className={`rule-engine-v2__rule-group-list`}>
          {ruleEngine.state.ruleGroups.map((ruleGroup, groupIndex) => {
            return (
              <React.Fragment key={ruleGroup.internalId}>
                <div
                  className={`rule-engine-v2__rule-group ${ruleGroup.addedFromPredictedSegment ? 'rule-engine-v2__rule-group-for-predicted-segment' : ''}`}>
                  <div>
                    {ruleGroup.rules?.length > 1 && (
                      <div className={'rule-engine-v2__match-all-any medium'}>
                        <div
                          onClick={() => ruleEngine.toggleMatchAllRules(ruleGroup.internalId, true)}
                          className={`rule-engine-v2__match-all-any__button ${!bindings.viewMode && 'clickable'} ${ruleGroup.matchAllRules && 'selected'}`}>
                          Match all rules
                        </div>
                        <div
                          onClick={() => ruleEngine.toggleMatchAllRules(ruleGroup.internalId, false)}
                          className={`rule-engine-v2__match-all-any__button ${!bindings.viewMode && 'clickable'} ${!ruleGroup.matchAllRules && 'selected'}`}>
                          Match any rule
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="rule-engine-v2__rules">
                    {ruleGroup.rules?.length > 0 && (
                      <div className="rule-engine-v2__rules-list">
                        {ruleGroup.rules?.map((rule, ruleIndex) => {
                          return (
                            <React.Fragment key={rule.internalId}>
                              <div className="rule-engine-v2__rules-list__item">
                                <div className="rule-engine-v2__rules-list__item__property">
                                  <Dropdown
                                    disabled={bindings.viewMode}
                                    options={config.properties.map((prop) => {
                                      return {
                                        label: prop.property,
                                        value: prop.property,
                                        measuringUnit: prop.measuringUnit,
                                      };
                                    })}
                                    showValidation={bindings.showValidationErrors}
                                    onSelect={(option: Option & { measuringUnit?: string }) => {
                                      ruleEngine.setRuleProperty(
                                        ruleGroup.internalId,
                                        rule.internalId,
                                        option.value,
                                        option.measuringUnit,
                                      );
                                    }}
                                    preSelectedOption={{
                                      label: rule?.property || 'Choose',
                                      value: rule.property || undefined,
                                    }}
                                  />
                                </div>
                                <div className="rule-engine-v2__rules-list__item__operator">
                                  <Dropdown
                                    key={JSON.stringify(rule)}
                                    options={
                                      rule.property
                                        ? ruleEngine.findOperatorsBasedOnProperty(rule.property)?.map((op) => {
                                            return {
                                              label: op,
                                              value: op,
                                            };
                                          })
                                        : []
                                    }
                                    showValidation={bindings.showValidationErrors}
                                    disabled={!rule.property || bindings.viewMode}
                                    preSelectedOption={{
                                      label: rule.operator || 'Choose',
                                      value: rule.operator || undefined,
                                    }}
                                    onSelect={(operatorOption: Option) => {
                                      ruleEngine.setRuleOperator(
                                        ruleGroup.internalId,
                                        rule.internalId,
                                        operatorOption.value,
                                      );
                                    }}
                                  />
                                </div>
                                <div
                                  key={JSON.stringify(rule.property)}
                                  className="rule-engine-v2__rules-list__item__value">
                                  {ruleEngine.renderRuleInputField(ruleGroup.internalId, rule)}
                                </div>
                                {!bindings.viewMode && (
                                  <div className="rule-engine-v2__rules-list__item__remove">
                                    <Tooltip
                                      tooltipText={'Delete rule'}
                                      appearOnSide={config.visualConfig?.tooltipAppearOnAltSide}>
                                      <div
                                        onClick={() =>
                                          ruleEngine.removeRuleFromGroup(ruleGroup.internalId, rule.internalId)
                                        }
                                        className="rule-engine-v2__remove-button">
                                        <i className="icon icon-close rule-engine-v2__remove-icon"></i>
                                      </div>
                                    </Tooltip>
                                  </div>
                                )}
                              </div>
                              {ruleEngine.renderCustomerOrderCountTimerFilters(ruleGroup.internalId, rule)}
                              {ruleIndex < ruleGroup.rules.length - 1 && (
                                <div className="rule-engine-v2__rules-list__connector small">
                                  {ruleGroup.matchAllRules ? 'And' : 'Or'}
                                </div>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className={'rule-engine-v2__rule-actions'}>
                    {!bindings.viewMode && (
                      <Button
                        onClick={() => ruleEngine.addBlankRule(ruleGroup.internalId)}
                        text={'+ Add rule'}
                        size={'medium'}
                        variant={'tertiary'}
                      />
                    )}
                    {ruleGroup.rules.length === 0 && !bindings.viewMode && (
                      <Tooltip tooltipText={'Delete group'} appearOnSide={config.visualConfig?.tooltipAppearOnAltSide}>
                        <div
                          onClick={() => ruleEngine.removeRuleGroup(ruleGroup.internalId)}
                          className="rule-engine-v2__remove-button">
                          <i className="icon icon-close rule-engine-v2__remove-icon"></i>
                        </div>
                      </Tooltip>
                    )}
                  </div>
                </div>
                {groupIndex < ruleEngine.state.ruleGroups.length - 1 && (
                  <div className="rule-engine-v2__rules-list__connector medium full-padding">
                    {`${ruleEngine.state.matchAllRuleGroups ? 'And' : 'Or'}${ruleEngine.state.ruleGroups[groupIndex + 1]?.addedFromPredictedSegment === true ? ' (Predicted Segment)' : ''}`}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
      {!bindings.viewMode && (
        <Button
          className="rule-engine-v2__add-rule-group-button"
          onClick={() => {
            ruleEngine.addBlankRuleGroup();
          }}
          text={'+ Add rule group'}
          size={'medium'}
          variant={'tertiary'}
        />
      )}
    </div>
  );
};
